import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
const API_BASE = import.meta.env.VITE_API_BASE;
import '../styles/Settings.css';

const OPENAI_MODELS = [
  { value: 'gpt-4o',            label: 'GPT-4o (recommended)' },
  { value: 'gpt-4o-mini',       label: 'GPT-4o Mini (faster, cheaper)' },
  { value: 'gpt-4-turbo',       label: 'GPT-4 Turbo' },
  { value: 'gpt-3.5-turbo',     label: 'GPT-3.5 Turbo (fastest)' },
];

const ANTHROPIC_MODELS = [
  { value: 'claude-haiku-4-5',           label: 'Claude Haiku 4.5 (recommended)' },
  { value: 'claude-opus-4-6',            label: 'Claude Opus 4.6 (most capable)' },
  { value: 'claude-opus-4-5',            label: 'Claude Opus 4.5' },
  { value: 'claude-sonnet-4-5',          label: 'Claude Sonnet 4.5' },
  { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
  { value: 'claude-3-5-haiku-20241022',  label: 'Claude 3.5 Haiku' },
  { value: 'claude-3-opus-20240229',     label: 'Claude 3 Opus' },
];

function ProviderCard({ id, name, logo, keyLabel, keyPlaceholder, models, data, onChange, onSave, onClear, saving, testResult, onTest }) {
  const [showKey, setShowKey] = useState(false);

  return (
    <div className={`st-provider-card ${data.configured ? 'st-card-active' : ''}`}>
      <div className="st-card-header">
        <div className="st-card-title">
          <span className="st-provider-logo">{logo}</span>
          <span className="st-provider-name">{name}</span>
        </div>
        <span className={`st-status-pill ${data.configured ? 'st-status-ok' : 'st-status-off'}`}>
          {data.configured ? 'Configured' : 'Not configured'}
        </span>
      </div>

      <div className="st-card-body">
        <div className="st-field">
          <label className="st-label">{keyLabel}</label>
          <div className="st-key-row">
            <input
              className="st-input st-key-input"
              type={showKey ? 'text' : 'password'}
              placeholder={data.configured ? data.key_hint || 'sk-…' : keyPlaceholder}
              value={data.newKey || ''}
              onChange={e => onChange({ newKey: e.target.value })}
              autoComplete="off"
            />
            <button className="st-eye-btn" onClick={() => setShowKey(v => !v)} title={showKey ? 'Hide key' : 'Show key'}>
              {showKey
                ? <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z"/><circle cx="10" cy="10" r="2.5"/><line x1="3" y1="3" x2="17" y2="17"/></svg>
                : <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z"/><circle cx="10" cy="10" r="2.5"/></svg>
              }
            </button>
            {data.configured && (
              <button className="st-clear-btn" onClick={onClear} title={`Remove ${name} key`}>
                <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/></svg>
              </button>
            )}
          </div>
          {data.configured && !data.newKey && (
            <div className="st-key-hint">Current: {data.key_hint} &nbsp;·&nbsp; Enter a new key above to replace it</div>
          )}
        </div>

        <div className="st-field">
          <label className="st-label">Default model</label>
          <select className="st-select" value={data.model} onChange={e => onChange({ model: e.target.value })}>
            {models.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        {testResult && (
          <div className={`st-test-result ${testResult.ok ? 'st-test-ok' : 'st-test-err'}`}>
            {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
          </div>
        )}
      </div>

      <div className="st-card-footer">
        <button className="st-btn-ghost" onClick={onTest} disabled={!data.configured && !data.newKey}>
          Test connection
        </button>
        <button className="st-btn-primary" onClick={onSave} disabled={saving || (!data.newKey && data.configured)}>
          {saving ? 'Saving…' : data.configured ? 'Update key' : 'Save key'}
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { apiFetch } = useAuth();

  const [openai, setOpenai] = useState({ configured: false, key_hint: '', model: 'gpt-4o', newKey: '' });
  const [anthropic, setAnthropic] = useState({ configured: false, key_hint: '', model: 'claude-haiku-4-5', newKey: '' });
  const [defaultProvider, setDefaultProvider] = useState('openai');
  const [saving, setSaving] = useState({ openai: false, anthropic: false, general: false });
  const [testResults, setTestResults] = useState({});
  const [generalSaved, setGeneralSaved] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    apiFetch(`${API_BASE}/settings`)
      .then(r => r.json())
      .then(d => {
        setOpenai(prev => ({ ...prev, configured: d.openai.configured, key_hint: d.openai.key_hint, model: d.openai.model }));
        setAnthropic(prev => ({ ...prev, configured: d.anthropic.configured, key_hint: d.anthropic.key_hint, model: d.anthropic.model }));
        setDefaultProvider(d.default_provider || 'openai');
      })
      .catch(() => setLoadError('Could not load settings'));
  }, [apiFetch]);

  const saveProvider = async (provider) => {
    setSaving(s => ({ ...s, [provider]: true }));
    const state = provider === 'openai' ? openai : anthropic;
    const body = {};
    if (state.newKey) body[`${provider}_api_key`] = state.newKey;
    body[`${provider}_model`] = state.model;

    try {
      const res = await apiFetch(`${API_BASE}/settings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Save failed'); }
      const setter = provider === 'openai' ? setOpenai : setAnthropic;
      setter(prev => ({ ...prev, configured: true, newKey: '', key_hint: prev.newKey ? prev.newKey.slice(0, 6) + '…' + prev.newKey.slice(-4) : prev.key_hint }));
      setTestResults(r => ({ ...r, [provider]: null }));
    } catch (e) {
      setTestResults(r => ({ ...r, [provider]: { ok: false, message: e.message } }));
    }
    setSaving(s => ({ ...s, [provider]: false }));
  };

  const clearProvider = async (provider) => {
    const res = await apiFetch(`${API_BASE}/settings/key/${provider}`, { method: 'DELETE' });
    if (res.ok) {
      const setter = provider === 'openai' ? setOpenai : setAnthropic;
      setter(prev => ({ ...prev, configured: false, key_hint: '', newKey: '' }));
      setTestResults(r => ({ ...r, [provider]: null }));
    }
  };

  const testProvider = async (provider) => {
    const state = provider === 'openai' ? openai : anthropic;
    if (!state.configured && !state.newKey) return;
    setTestResults(r => ({ ...r, [provider]: { ok: null, message: 'Testing…' } }));
    try {
      const res = await apiFetch(`${API_BASE}/settings/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, key: state.newKey || undefined, model: state.model }),
      });
      const d = await res.json();
      setTestResults(r => ({ ...r, [provider]: { ok: d.success, message: d.message } }));
    } catch {
      setTestResults(r => ({ ...r, [provider]: { ok: false, message: 'Network error' } }));
    }
  };

  const saveGeneral = async () => {
    setSaving(s => ({ ...s, general: true }));
    try {
      await apiFetch(`${API_BASE}/settings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ default_provider: defaultProvider }) });
      setGeneralSaved(true);
      setTimeout(() => setGeneralSaved(false), 2500);
    } catch {}
    setSaving(s => ({ ...s, general: false }));
  };

  return (
    <div className="st-page">
      <div className="st-header">
        <h1 className="st-title">Settings</h1>
        <p className="st-subtitle">Configure API keys and generation defaults. Keys are stored per workspace and never exposed in full after saving.</p>
      </div>

      {loadError && <div className="st-load-error">{loadError}</div>}

      {/* ── AI Configuration ── */}
      <section className="st-section">
        <div className="st-section-header">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="10" cy="10" r="8"/><path d="M7 10l2 2 4-4"/>
          </svg>
          <h2 className="st-section-title">AI Provider Configuration</h2>
        </div>
        <p className="st-section-desc">
          Enable AI-powered data generation by connecting to OpenAI or Anthropic. Once configured, use the <strong>AI gen</strong> toggle on any field in the Design Studio to generate contextually realistic values.
        </p>

        <div className="st-providers-grid">
          <ProviderCard
            id="openai"
            name="OpenAI"
            logo={<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M22.28 9.84a5.9 5.9 0 0 0-.52-4.91 6.14 6.14 0 0 0-6.6-2.9A6.14 6.14 0 0 0 10.54 0a6.14 6.14 0 0 0-5.87 4.25 5.9 5.9 0 0 0-3.95 2.87 6.14 6.14 0 0 0 .76 7.19 5.9 5.9 0 0 0 .52 4.9 6.14 6.14 0 0 0 6.6 2.9A6.14 6.14 0 0 0 13.46 24a6.14 6.14 0 0 0 5.87-4.25 5.9 5.9 0 0 0 3.95-2.87 6.14 6.14 0 0 0-.76-7.19z"/></svg>}
            keyLabel="API Key"
            keyPlaceholder="sk-…"
            models={OPENAI_MODELS}
            data={openai}
            onChange={updates => setOpenai(prev => ({ ...prev, ...updates }))}
            onSave={() => saveProvider('openai')}
            onClear={() => clearProvider('openai')}
            onTest={() => testProvider('openai')}
            saving={saving.openai}
            testResult={testResults.openai}
          />
          <ProviderCard
            id="anthropic"
            name="Anthropic (Claude)"
            logo={<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M13.827 3.52h-3.27L5 20.48h3.382l1.312-3.627h5.8l1.311 3.627H20L13.827 3.52zm-3.044 10.666 1.871-5.183 1.87 5.183H10.783z"/></svg>}
            keyLabel="API Key"
            keyPlaceholder="sk-ant-…"
            models={ANTHROPIC_MODELS}
            data={anthropic}
            onChange={updates => setAnthropic(prev => ({ ...prev, ...updates }))}
            onSave={() => saveProvider('anthropic')}
            onClear={() => clearProvider('anthropic')}
            onTest={() => testProvider('anthropic')}
            saving={saving.anthropic}
            testResult={testResults.anthropic}
          />
        </div>
      </section>

      {/* ── General ── */}
      <section className="st-section">
        <div className="st-section-header">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="10" cy="10" r="2.5"/><path d="M10 2v2m0 12v2m-5.66-3.34 1.42-1.42m8.48-8.48 1.42-1.42M2 10h2m12 0h2m-3.34 5.66-1.42-1.42M5.76 5.76 4.34 4.34"/>
          </svg>
          <h2 className="st-section-title">General</h2>
        </div>

        <div className="st-general-grid">
          <div className="st-field">
            <label className="st-label">Default AI provider</label>
            <p className="st-field-hint">Used when generating data with AI gen enabled and no provider is specified per-field.</p>
            <div className="st-provider-chooser">
              {[{ value: 'openai', label: 'OpenAI' }, { value: 'anthropic', label: 'Anthropic (Claude)' }].map(p => (
                <button
                  key={p.value}
                  className={`st-provider-choice ${defaultProvider === p.value ? 'active' : ''} ${!(p.value === 'openai' ? openai : anthropic).configured ? 'st-choice-disabled' : ''}`}
                  onClick={() => setDefaultProvider(p.value)}
                  title={!(p.value === 'openai' ? openai : anthropic).configured ? `${p.label} not configured` : ''}
                >
                  {p.label}
                  {!(p.value === 'openai' ? openai : anthropic).configured && <span className="st-choice-badge">Not set</span>}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="st-general-save">
          <button className="st-btn-primary" onClick={saveGeneral} disabled={saving.general}>
            {saving.general ? 'Saving…' : 'Save preferences'}
          </button>
          {generalSaved && <span className="st-saved-flash">✓ Saved</span>}
        </div>
      </section>
    </div>
  );
}
